import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Upload, X, Plus, Trash2, Loader2, ChevronDown, Image as ImageIcon,
  FileText, Megaphone, Clock, Check, AlertTriangle, ArrowLeft, Pencil
} from 'lucide-react';
import axios from 'axios';
import { getApiUrl } from '../utils';

const API_URL = getApiUrl();

const ARTICLE_CATEGORIES = [
  'outfit', 'shoes', 'accessories', 'beauty', 'streetwear', 'editorial', 'runway', 'trends'
];

export default function AdminPublish() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Auth gate
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);

  // Tab: 'articles' | 'promoters'
  const [activeTab, setActiveTab] = useState('articles');

  // ── Style Picks state ──
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [savingArticle, setSavingArticle] = useState(false);
  const [editingArticleId, setEditingArticleId] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: '', description: '', images: [], category: 'editorial', tags: [], tagInput: ''
  });

  // ── Promoters state ──
  const [promoters, setPromoters] = useState([]);
  const [loadingPromoters, setLoadingPromoters] = useState(true);
  const [showPromoterForm, setShowPromoterForm] = useState(false);
  const [savingPromoter, setSavingPromoter] = useState(false);
  const [editingPromoterId, setEditingPromoterId] = useState(null);
  const [promoterForm, setPromoterForm] = useState({
    brand_name: '', description: '', logo_url: '', website_url: '', contact_email: '',
    featured_products: [], productInput: { name: '', price: '', image: '' }
  });

  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const [authError, setAuthError] = useState(null);

  // ── Auth check ──
  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    axios.get(`${API_URL}/style-picks/all`, { headers })
      .then(() => { setAuthorized(true); setChecking(false); })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 403) {
          setAuthError('Admin access required. This account is not an admin.');
        } else if (status === 401) {
          setAuthError('Session expired. Please log in again.');
        } else {
          setAuthError('Could not connect to the server. Please try again.');
        }
        setChecking(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch data ──
  const fetchArticles = useCallback(async () => {
    setLoadingArticles(true);
    try {
      const res = await axios.get(`${API_URL}/style-picks/all`, { headers });
      setArticles(res.data.picks || []);
    } catch { setArticles([]); }
    finally { setLoadingArticles(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPromoters = useCallback(async () => {
    setLoadingPromoters(true);
    try {
      const res = await axios.get(`${API_URL}/promoters/all`, { headers });
      setPromoters(res.data.promoters || []);
    } catch { setPromoters([]); }
    finally { setLoadingPromoters(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authorized) { fetchArticles(); fetchPromoters(); }
  }, [authorized, fetchArticles, fetchPromoters]);

  // ── Helpers ──
  const getCurrentWeek = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const weekNum = Math.ceil(((now - start) / 86400000 + start.getDay() + 1) / 7);
    return `Week ${weekNum}, ${now.getFullYear()}`;
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  // ── Article handlers ──
  const handleArticleImages = (e) => {
    Array.from(e.target.files).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => setArticleForm(p => ({ ...p, images: [...p.images, reader.result] }));
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeArticleImage = (idx) =>
    setArticleForm(p => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));

  const addArticleTag = () => {
    const t = articleForm.tagInput.trim();
    if (t && !articleForm.tags.includes(t)) {
      setArticleForm(p => ({ ...p, tags: [...p.tags, t], tagInput: '' }));
    }
  };

  const resetArticleForm = () => {
    setArticleForm({ title: '', description: '', images: [], category: 'editorial', tags: [], tagInput: '' });
    setEditingArticleId(null);
  };

  const startEditArticle = (a) => {
    setEditingArticleId(a.id);
    setArticleForm({
      title: a.title || '',
      description: a.description || '',
      images: a.images || (a.image_url ? [a.image_url] : []),
      category: a.category || 'editorial',
      tags: a.tags || [],
      tagInput: ''
    });
    setShowArticleForm(true);
  };

  const publishArticle = async () => {
    if (!articleForm.title.trim() || articleForm.images.length === 0) return;
    setSavingArticle(true);
    try {
      const payload = {
        title: articleForm.title.trim(),
        description: articleForm.description.trim(),
        images: articleForm.images,
        tags: articleForm.tags,
        category: articleForm.category,
        week_label: getCurrentWeek()
      };
      if (editingArticleId) {
        await axios.put(`${API_URL}/style-picks/${editingArticleId}`, payload, { headers });
        showToast('Article updated!');
      } else {
        await axios.post(`${API_URL}/style-picks`, payload, { headers });
        showToast('Article published!');
      }
      resetArticleForm();
      setShowArticleForm(false);
      fetchArticles();
    } catch (err) { const e = err.response?.data?.error; showToast(typeof e === 'object' ? (e.message || 'Failed to save') : (e || 'Failed to save'), 'error'); }
    finally { setSavingArticle(false); }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Delete this article? Users will no longer see it.')) return;
    try {
      await axios.delete(`${API_URL}/style-picks/${id}`, { headers });
      setArticles(prev => prev.filter(a => a.id !== id));
      showToast('Article deleted');
    } catch { showToast('Failed to delete', 'error'); }
  };

  // ── Promoter handlers ──
  const handlePromoterLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPromoterForm(p => ({ ...p, logo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const handlePromoterProductImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPromoterForm(p => ({
      ...p, productInput: { ...p.productInput, image: reader.result }
    }));
    reader.readAsDataURL(file);
  };

  const addPromoterProduct = () => {
    if (!promoterForm.productInput.name) return;
    setPromoterForm(p => ({
      ...p,
      featured_products: [...p.featured_products, { ...p.productInput, id: Date.now() }],
      productInput: { name: '', price: '', image: '' }
    }));
  };

  const resetPromoterForm = () => {
    setPromoterForm({
      brand_name: '', description: '', logo_url: '', website_url: '', contact_email: '',
      featured_products: [], productInput: { name: '', price: '', image: '' }
    });
    setEditingPromoterId(null);
  };

  const startEditPromoter = (p) => {
    setEditingPromoterId(p.id);
    setPromoterForm({
      brand_name: p.brand_name || '',
      description: p.description || '',
      logo_url: p.logo_url || '',
      website_url: p.website_url || '',
      contact_email: p.contact_email || '',
      featured_products: Array.isArray(p.featured_products) ? p.featured_products : [],
      productInput: { name: '', price: '', image: '' }
    });
    setShowPromoterForm(true);
  };

  const addPromoter = async () => {
    if (!promoterForm.brand_name.trim()) return;
    setSavingPromoter(true);
    try {
      const { productInput, ...data } = promoterForm;
      // Auto-include any in-progress product that hasn't been added yet
      if (productInput && (productInput.name || productInput.image)) {
        data.featured_products = [...data.featured_products, { ...productInput, id: Date.now() }];
      }
      if (editingPromoterId) {
        await axios.put(`${API_URL}/promoters/${editingPromoterId}`, { ...data, is_active: true }, { headers });
        showToast('Promoter updated!');
      } else {
        await axios.post(`${API_URL}/promoters`, data, { headers });
        showToast('Promoter added!');
      }
      resetPromoterForm();
      setShowPromoterForm(false);
      fetchPromoters();
    } catch (err) { const e = err.response?.data?.error; showToast(typeof e === 'object' ? (e.message || 'Failed to save') : (e || 'Failed to save'), 'error'); }
    finally { setSavingPromoter(false); }
  };

  const deletePromoter = async (id) => {
    if (!window.confirm('Remove this promoter?')) return;
    try {
      await axios.delete(`${API_URL}/promoters/${id}`, { headers });
      setPromoters(prev => prev.filter(p => p.id !== id));
      showToast('Promoter removed');
    } catch { showToast('Failed to delete', 'error'); }
  };

  // ── Loading / Auth gate ──
  if (checking) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  if (authError || !authorized) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-stone-800 mb-2">Access Denied</h2>
          <p className="text-sm text-stone-500 mb-6">{authError || 'You do not have permission to access this page.'}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/home')}
              className="px-4 py-2.5 border border-stone-200 rounded-lg text-sm text-stone-600 hover:bg-stone-100"
            >
              Go Home
            </button>
            <button
              onClick={() => { localStorage.removeItem('token'); navigate('/login'); }}
              className="px-4 py-2.5 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-800"
            >
              Log In Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* ── Header ── */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/home')} className="p-2 hover:bg-stone-100 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-stone-500" />
              </button>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-terracotta-400 to-amber-400 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                fD
              </div>
              <div>
                <h1 className="text-xl font-bold text-stone-900">Publisher Dashboard</h1>
                <p className="text-xs text-stone-400">Manage your articles and promoters</p>
              </div>
            </div>
            <span className="text-xs bg-terracotta-100 text-terracotta-700 px-3 py-1 rounded-full font-medium">
              Admin Only
            </span>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex gap-0">
          {[
            { key: 'articles', label: 'Style Picks', icon: FileText, count: articles.length },
            { key: 'promoters', label: 'Promoters', icon: Megaphone, count: promoters.length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-stone-900 text-stone-900'
                  : 'border-transparent text-stone-400 hover:text-stone-600'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-500'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 pb-24">

        {/* ═══════════════════════════════════════════════ */}
        {/* ── ARTICLES TAB ── */}
        {/* ═══════════════════════════════════════════════ */}
        {activeTab === 'articles' && (
          <div>
            {/* Action bar */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-stone-800">Your Articles</h2>
                <p className="text-xs text-stone-400">Latest 8 are shown to users · Older ones auto-rotate out</p>
              </div>
              <button
                onClick={() => { resetArticleForm(); setShowArticleForm(true); }}
                className="flex items-center gap-2 px-4 py-2.5 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-800 transition-colors"
              >
                <Plus className="w-4 h-4" /> New Article
              </button>
            </div>

            {/* Article form */}
            <AnimatePresence>
              {showArticleForm && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="mb-6"
                >
                  <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h3 className="font-bold text-stone-800 mb-4">{editingArticleId ? 'Edit Article' : 'Publish New Article'}</h3>

                    <div className="space-y-4">
                      {/* Images */}
                      <div>
                        <label className="block text-sm font-medium text-stone-600 mb-2">Photos (first = cover)</label>
                        <div className="flex gap-2 flex-wrap">
                          {articleForm.images.map((img, i) => (
                            <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden bg-stone-100">
                              <img src={img} alt="" className="w-full h-full object-cover" />
                              <button onClick={() => removeArticleImage(i)}
                                className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 text-white rounded-full">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          <label className="w-20 h-20 rounded-lg border-2 border-dashed border-stone-200 hover:border-terracotta-300 cursor-pointer flex items-center justify-center">
                            <Upload className="w-5 h-5 text-stone-300" />
                            <input type="file" accept="image/*" multiple className="hidden" onChange={handleArticleImages} />
                          </label>
                        </div>
                      </div>

                      {/* Title + Category */}
                      <div className="grid sm:grid-cols-[1fr,auto] gap-3">
                        <div>
                          <label className="block text-sm font-medium text-stone-600 mb-1">Headline</label>
                          <input
                            value={articleForm.title}
                            onChange={e => setArticleForm(p => ({ ...p, title: e.target.value }))}
                            placeholder="The Art of Layering: Spring 2026's Boldest Trend"
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-terracotta-400 focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-stone-600 mb-1">Section</label>
                          <div className="relative">
                            <select
                              value={articleForm.category}
                              onChange={e => setArticleForm(p => ({ ...p, category: e.target.value }))}
                              className="appearance-none w-full px-3 py-2.5 border border-stone-200 rounded-lg bg-white text-sm pr-8 focus:ring-2 focus:ring-terracotta-400"
                            >
                              {ARTICLE_CATEGORIES.map(c => (
                                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                              ))}
                            </select>
                            <ChevronDown className="w-4 h-4 text-stone-400 absolute right-2.5 top-3 pointer-events-none" />
                          </div>
                        </div>
                      </div>

                      {/* Body */}
                      <div>
                        <label className="block text-sm font-medium text-stone-600 mb-1">Article Body</label>
                        <textarea
                          value={articleForm.description}
                          onChange={e => setArticleForm(p => ({ ...p, description: e.target.value }))}
                          rows={5}
                          placeholder="Write your article body here. Use line breaks for paragraphs."
                          className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-terracotta-400 focus:border-transparent text-sm"
                        />
                      </div>

                      {/* Tags */}
                      <div>
                        <label className="block text-sm font-medium text-stone-600 mb-1">Tags</label>
                        <div className="flex gap-2">
                          <input
                            value={articleForm.tagInput}
                            onChange={e => setArticleForm(p => ({ ...p, tagInput: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addArticleTag(); } }}
                            placeholder="Add tag"
                            className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-terracotta-400"
                          />
                          <button onClick={addArticleTag} className="px-3 py-2 bg-stone-100 rounded-lg text-sm hover:bg-stone-200">Add</button>
                        </div>
                        {articleForm.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {articleForm.tags.map(t => (
                              <span key={t} className="text-xs px-2 py-1 bg-stone-100 text-stone-600 rounded-md flex items-center gap-1">
                                #{t}
                                <button onClick={() => setArticleForm(p => ({ ...p, tags: p.tags.filter(x => x !== t) }))}>
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-stone-100">
                      <button onClick={() => { setShowArticleForm(false); resetArticleForm(); }}
                        className="px-4 py-2 text-stone-500 hover:bg-stone-100 rounded-lg text-sm">Cancel</button>
                      <button
                        onClick={publishArticle}
                        disabled={!articleForm.title.trim() || articleForm.images.length === 0 || savingArticle}
                        className="px-5 py-2 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-800 disabled:opacity-40 flex items-center gap-2"
                      >
                        {savingArticle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        {editingArticleId ? 'Update' : 'Publish'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Articles list */}
            {loadingArticles ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
            ) : articles.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-stone-200">
                <ImageIcon className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500 font-medium">No articles published yet</p>
                <p className="text-stone-400 text-sm mt-1">Click "New Article" to publish your first weekly pick.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {articles.map((a, i) => (
                  <div key={a.id} className="bg-white rounded-xl border border-stone-200 p-4 flex gap-4 items-center">
                    {/* Thumbnail */}
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-stone-100 flex-shrink-0">
                      {(a.images?.[0] || a.image_url) ? (
                        <img src={a.images?.[0] || a.image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-6 h-6 text-stone-300" /></div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-bold uppercase tracking-wide text-terracotta-600">{a.category}</span>
                        {i < 8 && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Live</span>}
                        {i >= 8 && <span className="text-xs bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded font-medium">Rotated out</span>}
                      </div>
                      <h4 className="font-semibold text-stone-800 text-sm truncate">{a.title}</h4>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-stone-400">
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(a.published_at)}</span>
                        <span>·</span>
                        <span>{a.week_label}</span>
                        {a.images?.length > 1 && <span>· {a.images.length} photos</span>}
                      </div>
                    </div>

                    {/* Edit + Delete */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => startEditArticle(a)}
                        className="p-2 text-stone-400 hover:text-terracotta-600 hover:bg-terracotta-50 rounded-lg transition-colors"
                        title="Edit article"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteArticle(a.id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete article"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════ */}
        {/* ── PROMOTERS TAB ── */}
        {/* ═══════════════════════════════════════════════ */}
        {activeTab === 'promoters' && (
          <div>
            {/* Action bar */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-stone-800">Your Promoters</h2>
                <p className="text-xs text-stone-400">Active brands shown on the Promoters page</p>
              </div>
              <button
                onClick={() => { resetPromoterForm(); setShowPromoterForm(true); }}
                className="flex items-center gap-2 px-4 py-2.5 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-800 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add Brand
              </button>
            </div>

            {/* Promoter form */}
            <AnimatePresence>
              {showPromoterForm && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="mb-6"
                >
                  <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h3 className="font-bold text-stone-800 mb-4">{editingPromoterId ? 'Edit Brand' : 'Add New Brand'}</h3>

                    <div className="space-y-4">
                      {/* Name */}
                      <div>
                        <label className="block text-sm font-medium text-stone-600 mb-1">Brand Name *</label>
                        <input
                          value={promoterForm.brand_name}
                          onChange={e => setPromoterForm(p => ({ ...p, brand_name: e.target.value }))}
                          placeholder="e.g. Urban Thread Co."
                          className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-terracotta-400"
                        />
                      </div>

                      {/* Logo Upload */}
                      <div>
                        <label className="block text-sm font-medium text-stone-600 mb-2">Brand Logo</label>
                        {promoterForm.logo_url ? (
                          <div className="relative inline-block">
                            <img src={promoterForm.logo_url} alt="" className="w-28 h-28 rounded-xl object-cover border border-stone-200" />
                            <button onClick={() => setPromoterForm(p => ({ ...p, logo_url: '' }))}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <label className="w-28 h-28 rounded-xl border-2 border-dashed border-stone-300 hover:border-terracotta-400 cursor-pointer flex flex-col items-center justify-center gap-1.5 transition-colors">
                            <Upload className="w-5 h-5 text-stone-400" />
                            <span className="text-xs text-stone-400">Upload logo</span>
                            <input type="file" accept="image/*" className="hidden" onChange={handlePromoterLogo} />
                          </label>
                        )}
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-stone-600 mb-1">Description</label>
                        <textarea
                          value={promoterForm.description}
                          onChange={e => setPromoterForm(p => ({ ...p, description: e.target.value }))}
                          rows={2}
                          placeholder="Tell us about this brand..."
                          className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-terracotta-400"
                        />
                      </div>

                      {/* Website + Email */}
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-stone-600 mb-1">Website</label>
                          <input
                            value={promoterForm.website_url}
                            onChange={e => setPromoterForm(p => ({ ...p, website_url: e.target.value }))}
                            placeholder="https://..."
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-terracotta-400"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-stone-600 mb-1">Contact Email</label>
                          <input
                            value={promoterForm.contact_email}
                            onChange={e => setPromoterForm(p => ({ ...p, contact_email: e.target.value }))}
                            placeholder="brand@email.com"
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-terracotta-400"
                          />
                        </div>
                      </div>

                      {/* Featured Products */}
                      <div>
                        <label className="block text-sm font-medium text-stone-600 mb-2">Featured Products</label>
                        {promoterForm.featured_products.length > 0 && (
                          <div className="space-y-1.5 mb-3">
                            {promoterForm.featured_products.map((p, i) => (
                              <div key={p.id || i} className="flex items-center gap-3 bg-stone-50 rounded-lg px-3 py-2">
                                {p.image && <img src={p.image} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                                <span className="flex-1 text-sm font-medium truncate">{p.name}</span>
                                {p.price && <span className="text-xs text-stone-500">{p.price}</span>}
                                <button onClick={() => setPromoterForm(prev => ({
                                  ...prev, featured_products: prev.featured_products.filter((_, fi) => fi !== i)
                                }))} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input
                            value={promoterForm.productInput.name}
                            onChange={e => setPromoterForm(p => ({ ...p, productInput: { ...p.productInput, name: e.target.value } }))}
                            placeholder="Product name"
                            className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-terracotta-400"
                          />
                          <input
                            value={promoterForm.productInput.price}
                            onChange={e => setPromoterForm(p => ({ ...p, productInput: { ...p.productInput, price: e.target.value } }))}
                            placeholder="Price"
                            className="w-20 px-3 py-2 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-terracotta-400"
                          />
                          <button onClick={addPromoterProduct}
                            disabled={!promoterForm.productInput.name}
                            className="px-3 py-2 bg-terracotta-500 text-white rounded-lg text-sm hover:bg-terracotta-600 disabled:opacity-40">
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Product image upload + preview */}
                        <div className="mt-2 flex items-center gap-3">
                          <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-terracotta-400 hover:bg-stone-50 transition-colors">
                            <Upload className="w-4 h-4 text-stone-400" />
                            <span className="text-xs text-stone-500">Add product photo</span>
                            <input type="file" accept="image/*" className="hidden" onChange={handlePromoterProductImage} />
                          </label>
                          {promoterForm.productInput.image && (
                            <div className="relative">
                              <img src={promoterForm.productInput.image} alt="" className="w-12 h-12 rounded-lg object-cover border border-stone-200" />
                              <button onClick={() => setPromoterForm(p => ({ ...p, productInput: { ...p.productInput, image: '' } }))}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-stone-100">
                      <button onClick={() => { setShowPromoterForm(false); resetPromoterForm(); }}
                        className="px-4 py-2 text-stone-500 hover:bg-stone-100 rounded-lg text-sm">Cancel</button>
                      <button
                        onClick={addPromoter}
                        disabled={!promoterForm.brand_name.trim() || savingPromoter}
                        className="px-5 py-2 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-800 disabled:opacity-40 flex items-center gap-2"
                      >
                        {savingPromoter ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        {editingPromoterId ? 'Update' : 'Add Brand'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Promoters list */}
            {loadingPromoters ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
            ) : promoters.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-stone-200">
                <Megaphone className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500 font-medium">No promoters yet</p>
                <p className="text-stone-400 text-sm mt-1">Click "Add Brand" to feature your first promoter.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {promoters.map(p => (
                  <div key={p.id} className="bg-white rounded-xl border border-stone-200 p-4 flex gap-4 items-center">
                    {/* Logo */}
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-stone-100 flex-shrink-0 flex items-center justify-center">
                      {p.logo_url ? (
                        <img src={p.logo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="font-bold text-stone-400 text-lg">{p.brand_name?.[0]}</span>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h4 className="font-semibold text-stone-800 text-sm">{p.brand_name}</h4>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          p.is_active ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'
                        }`}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {p.description && <p className="text-xs text-stone-400 truncate">{p.description}</p>}
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-stone-400">
                        {p.website_url && <span className="truncate max-w-[150px]">{p.website_url}</span>}
                        {p.featured_products && <span>· {Array.isArray(p.featured_products) ? p.featured_products.length : 0} products</span>}
                      </div>
                    </div>

                    {/* Edit + Delete */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => startEditPromoter(p)}
                        className="p-2 text-stone-400 hover:text-terracotta-600 hover:bg-terracotta-50 rounded-lg transition-colors"
                        title="Edit promoter"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deletePromoter(p.id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove promoter"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Toast ── */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium z-50 ${
              toast.type === 'error'
                ? 'bg-red-600 text-white'
                : 'bg-stone-900 text-white'
            }`}
          >
            {toast.type === 'error' ? <AlertTriangle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
